import { useState, useCallback } from "react";

interface UseGoogleLoginReturn {
  isLoading: boolean;
  error: string;
  loginWithGoogle: () => void;
  clearError: () => void;
}

const randomBytes = (len: number) => crypto.getRandomValues(new Uint8Array(len));

const base64url = (buf: ArrayBuffer | Uint8Array) =>
  btoa(
    String.fromCharCode(
      ...Array.from(new Uint8Array(buf instanceof ArrayBuffer ? new Uint8Array(buf) : buf))
    )
  )
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");

const genCodeVerifier = () => base64url(randomBytes(32));
const genNonce = () => base64url(randomBytes(16));

const sha256 = async (input: string) => {
  const enc = new TextEncoder().encode(input);
  const buf = await crypto.subtle.digest("SHA-256", enc);
  return base64url(buf);
};

const useGoogleLogin = (): UseGoogleLoginReturn => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  const loginWithGoogle = useCallback(async () => {
    setError("");
    setIsLoading(true);
    try {
      const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID!;
      if (!clientId) {
        throw new Error("환경변수 REACT_APP_GOOGLE_CLIENT_ID가 필요합니다.");
      }

      // 당신이 Google 콘솔에 등록한 콜백 URL
      const redirectUri = `${window.location.origin}/auth/google/callback`;
      const scope = "openid email profile";

      // PKCE + nonce 생성
      const codeVerifier = genCodeVerifier();
      const codeChallenge = await sha256(codeVerifier);
      const nonce = genNonce();
      const state = base64url(randomBytes(12));

      // 교환 시 사용할 값 보관
      sessionStorage.setItem("g_code_verifier", codeVerifier);
      sessionStorage.setItem("g_nonce", nonce);
      sessionStorage.setItem("g_redirect_url", redirectUri);

      const authUrl =
        `https://accounts.google.com/o/oauth2/v2/auth?` +
        `client_id=${encodeURIComponent(clientId)}&` +
        `redirect_uri=${encodeURIComponent(redirectUri)}&` +
        `response_type=code&` +
        `scope=${encodeURIComponent(scope)}&` +
        `prompt=select_account&` +
        `state=${encodeURIComponent(state)}&` +
        `nonce=${encodeURIComponent(nonce)}&` +
        `code_challenge=${encodeURIComponent(codeChallenge)}&` +
        `code_challenge_method=S256`;

      // 구글 로그인 리다이렉트 방식으로 변경
      window.location.href = authUrl;
    } catch (e: any) {
      setError(e.message || "구글 로그인 초기화 오류");
      setIsLoading(false);
    }
  }, []);

  const clearError = () => setError("");

  return { isLoading, error, loginWithGoogle, clearError };
};

export default useGoogleLogin;
